/**
 * Board collaboration core for the control-hub transport (ADR 0013). The wire protocol lives
 * in web/src/realtime (frames.ts / client.ts): editors send `board_update` client frames with
 * changed element snapshots only, the hub merges by element id and fans out to every other
 * member, and a late joiner receives the whole merged scene as `board_state`.
 *
 * This module is excalidraw-free and transport-free on purpose: it owns the per-broadcast
 * version memory (an element crosses the wire only when its version grew since the last
 * broadcast), the structural element gate, and the receiving side's last-known-scene memory
 * (BoardSceneBuffer) with the room's fan-out pipe around it (BoardSyncHub), so the wire
 * behavior stays unit-testable without the canvas or a socket.
 */

/** The sync payload a board canvas consumes: a delta or the whole persisted scene. */
export interface BoardSync {
  readonly kind: 'update' | 'state';
  readonly elements: readonly unknown[];
}

/**
 * Connection health of the board session - the room's realtime channel health, surfaced
 * verbatim: the board rides the /ws connection, it has no socket of its own since ADR 0013.
 */
export type BoardStatus = 'connecting' | 'live' | 'reconnecting' | 'closed';

/**
 * Structural element subset the version filter needs. Excalidraw elements carry monotonic
 * `version` counters; deleted elements stay as tombstones so deletions propagate.
 */
export interface BoardSyncElement {
  readonly id: string;
  readonly version: number;
}

/** True for the structural element subset the version filter and restore() can work with. */
export function isBoardSyncElement(value: unknown): value is BoardSyncElement {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).id === 'string' &&
    typeof (value as Record<string, unknown>).version === 'number'
  );
}

/**
 * Owns the outbound version memory of one board session (ADR 0013). `diff` picks the elements
 * whose version grew since the last successful broadcast - incrementally only the changed
 * entries go out, with `fullSync` the whole array (including deleted tombstones) does. The
 * memory resets when the board panel unmounts: a fresh mount starts from an empty delta
 * baseline, so its first local edit re-sends what changed since it appeared, and every new
 * connection gets the authoritative scene from the server's board_state anyway.
 */
export class BoardCollab {
  private readonly broadcastVersions = new Map<string, number>();

  /**
   * Filters one local scene change down to the snapshots worth sending: everything on a full
   * sync, otherwise only entries whose version grew past the last broadcast. Returns null when
   * an incremental change has nothing new - the caller sends nothing.
   */
  diff(
    elements: readonly BoardSyncElement[],
    fullSync: boolean,
  ): readonly BoardSyncElement[] | null {
    const syncable = elements.filter(
      (element) =>
        fullSync || element.version > (this.broadcastVersions.get(element.id) ?? -Infinity),
    );
    if (syncable.length === 0 && !fullSync) {
      return null;
    }
    for (const element of syncable) {
      this.broadcastVersions.set(element.id, element.version);
    }
    return syncable;
  }

  /** Clears the version memory (board panel unmounted; the next mount diffs from scratch). */
  reset(): void {
    this.broadcastVersions.clear();
  }
}

/** Extracts an element's merge key, or null when the value is not a scene element. */
export function boardElementId(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const id = (value as Record<string, unknown>).id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * Owns the client's last-known board scene: remote deltas merge by element id (last write
 * wins, deleted tombstones included so deletions replay), an authoritative board_state
 * snapshot replaces the whole thing. This memory is what a freshly mounted canvas rebuilds
 * its scene from when no handshake board_state is coming: the hub never echoes the sender's
 * own board_update, so without it a surface close would erase every stroke the local editor
 * made - the reopened board would render empty while the room lives on.
 */
export class BoardSceneBuffer {
  private readonly elements = new Map<string, unknown>();

  /** Merges one element batch: a remote delta, or the local editor's outbound diff. */
  apply(elements: readonly unknown[]): void {
    for (const element of elements) {
      const id = boardElementId(element);
      if (id !== null) {
        this.elements.set(id, element);
      }
    }
  }

  /** Replaces the whole scene with an authoritative board_state snapshot. */
  replace(elements: readonly unknown[]): void {
    this.elements.clear();
    this.apply(elements);
  }

  /** Forgets the scene (the room session ended; the next one starts from its handshake). */
  clear(): void {
    this.elements.clear();
  }

  /** The merged scene as an array, or null when nothing is known (nothing to replay). */
  snapshot(): readonly unknown[] | null {
    return this.elements.size > 0 ? [...this.elements.values()] : null;
  }
}

/** One mounted canvas's board sync subscription. */
export type BoardSyncListener = (sync: BoardSync) => void;

/**
 * The room's board fan-out pipe (ADR 0013): one instance per room session, holding the
 * last-known scene (BoardSceneBuffer) current from BOTH wire directions - remote deltas and
 * authoritative snapshots land whether or not a canvas is mounted, and the outbound diff is
 * recorded too because the hub never echoes the sender - and replaying that scene into every
 * canvas that subscribes. A closed surface only unsubscribes its canvas; a reopened one
 * re-subscribes and the buffered scene lands in the fresh editor through the same
 * pending-elements flush the handshake snapshot uses, so close-then-reopen never blanks the
 * board while the room session lives. reset() ends the session with the realtime teardown.
 */
export class BoardSyncHub {
  private readonly listeners = new Set<BoardSyncListener>();
  private readonly scene = new BoardSceneBuffer();

  /** One remote board_update delta: merged into the known scene, then fanned out. */
  applyUpdate(elements: readonly unknown[]): void {
    this.scene.apply(elements);
    this.fanOut({ kind: 'update', elements });
  }

  /** One authoritative board_state snapshot: replaces the known scene, then fans out. */
  applyState(elements: readonly unknown[]): void {
    this.scene.replace(elements);
    this.fanOut({ kind: 'state', elements });
  }

  /** Records the local editor's outbound diff (the hub does not echo the sender). */
  recordOutbound(elements: readonly BoardSyncElement[]): void {
    this.scene.apply(elements);
  }

  /**
   * Subscribes one mounting canvas: the known scene replays to it immediately (the same shape
   * a handshake board_state takes), then it lives on the live fan-out. Returns the
   * unsubscribe - a closed surface unsubscribes, a reopened one re-subscribes and the scene
   * replays again.
   */
  subscribe(listener: BoardSyncListener): () => void {
    this.listeners.add(listener);
    const known = this.scene.snapshot();
    if (known !== null) {
      listener({ kind: 'state', elements: known });
    }
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Ends the session: every canvas unsubscribes, the scene memory clears. */
  reset(): void {
    this.listeners.clear();
    this.scene.clear();
  }

  private fanOut(sync: BoardSync): void {
    for (const listener of [...this.listeners]) {
      listener(sync);
    }
  }
}
